<template>
  <main-template>
    <v-row align="center" class="mb-2">
      <v-col>
        <h1 class="text-h5 font-weight-bold d-flex align-center">
          <v-btn color="primary" x-small text class="mr-2" @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Relatório de Ponto Diário
        </h1>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-chip small outlined class="mr-2 rel-ponto__chip-data">
          <v-icon left x-small>mdi-calendar</v-icon>
          {{ dataLabel }}
        </v-chip>
        <v-btn
          color="primary"
          outlined
          small
          :loading="gerandoPdf"
          :disabled="!data"
          @click="gerarPdf"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Gerar PDF
        </v-btn>
      </v-col>
    </v-row>

    <v-card outlined class="mb-4">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <e-date-customize
              v-model="data"
              label="Data do relatório"
              outlined
              dense
              hide-details
              @change="buscar"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="busca"
              label="Filtrar colaborador"
              placeholder="Nome..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="4" md="3" class="d-flex">
            <v-btn
              color="primary"
              outlined
              dense
              class="mr-2"
              :loading="carregando"
              @click="buscar"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Buscar
            </v-btn>
            <v-btn color="primary" text dense @click="irParaHoje">Hoje</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <template v-if="carregado">
      <v-row class="mb-1">
        <v-col cols="6" md="3">
          <v-card outlined class="rel-ponto__stat rel-ponto__stat--total">
            <v-card-text class="py-3">
              <div class="rel-ponto__stat-label">Total de colaboradores</div>
              <div class="rel-ponto__stat-value">{{ resumo.total_colaboradores }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined class="rel-ponto__stat rel-ponto__stat--presentes">
            <v-card-text class="py-3">
              <div class="rel-ponto__stat-label">Presentes</div>
              <div class="rel-ponto__stat-value">
                {{ resumo.presentes }}
                <small>{{ formatarPercentual(resumo.presentes_percentual) }}</small>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined class="rel-ponto__stat rel-ponto__stat--ausentes">
            <v-card-text class="py-3">
              <div class="rel-ponto__stat-label">Sem registro</div>
              <div class="rel-ponto__stat-value">
                {{ resumo.ausentes }}
                <small>{{ formatarPercentual(resumo.ausentes_percentual) }}</small>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined class="rel-ponto__stat rel-ponto__stat--saidas">
            <v-card-text class="py-3">
              <div class="rel-ponto__stat-label">Saídas pendentes</div>
              <div class="rel-ponto__stat-value">{{ countSaidasPendentes }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card outlined class="rel-ponto__extremo">
            <v-card-text class="py-3 d-flex align-center">
              <div class="rel-ponto__extremo-icon rel-ponto__extremo-icon--first">
                <v-icon small color="white">mdi-arrow-down-bold</v-icon>
              </div>
              <div>
                <div class="rel-ponto__extremo-label">Primeiro a chegar</div>
                <div class="rel-ponto__extremo-valor">
                  {{ resumo.primeiro_chegar ? resumo.primeiro_chegar.nome : "—" }}
                </div>
              </div>
              <v-spacer />
              <v-chip
                v-if="resumo.primeiro_chegar"
                small
                dark
                color="success"
                class="font-weight-bold"
              >
                {{ resumo.primeiro_chegar.horario }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="rel-ponto__extremo">
            <v-card-text class="py-3 d-flex align-center">
              <div class="rel-ponto__extremo-icon rel-ponto__extremo-icon--last">
                <v-icon small color="white">mdi-arrow-up-bold</v-icon>
              </div>
              <div>
                <div class="rel-ponto__extremo-label">Último a chegar</div>
                <div class="rel-ponto__extremo-valor">
                  {{ resumo.ultimo_chegar ? resumo.ultimo_chegar.nome : "—" }}
                </div>
              </div>
              <v-spacer />
              <v-chip
                v-if="resumo.ultimo_chegar"
                small
                dark
                color="warning"
                class="font-weight-bold"
              >
                {{ resumo.ultimo_chegar.horario }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold py-3">
          <v-icon left>mdi-clipboard-text-outline</v-icon>
          Entradas e saídas
        </v-card-title>
        <v-data-table
          :headers="headersComputados"
          :items="colaboradoresFiltrados"
          :loading="carregando"
          :search="busca"
          no-data-text="Nenhum colaborador com registro neste dia"
          loading-text="Carregando relatório..."
          class="elevation-0"
        >
          <template #[`item.nome`]="{ item }">
            <div class="font-weight-bold">{{ item.nome }}</div>
          </template>

          <template v-for="n in maxPares" #[`item.entrada_${n}`]="{ item }">
            <v-chip :key="`e${n}`" x-small dark color="success" class="font-weight-bold">
              {{ item[`entrada_${n}`] || "—" }}
            </v-chip>
          </template>

          <template v-for="n in maxPares" #[`item.saida_${n}`]="{ item }">
            <v-chip :key="`s${n}`" x-small outlined color="grey darken-1">
              {{ item[`saida_${n}`] || "—" }}
            </v-chip>
          </template>

          <template #[`item.total_batidas`]="{ item }">
            <div class="d-flex align-center">
              <span class="font-weight-bold mr-2">{{ item.total_batidas }}</span>
              <v-icon v-if="item.saida_pendente" x-small color="warning" title="Saída pendente">
                mdi-clock-alert-outline
              </v-icon>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <v-card outlined>
        <v-card-title class="text-subtitle-1 font-weight-bold py-3">
          <v-icon left color="error">mdi-account-alert-outline</v-icon>
          Colaboradores sem registro
          <v-chip x-small dark color="error" class="ml-2 font-weight-bold">
            {{ ausentes.length }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="!ausentes.length" dense outlined type="info">
            Todos os colaboradores registraram ponto em {{ dataLabel }}.
          </v-alert>
          <div v-else class="d-flex flex-wrap">
            <v-chip
              v-for="item in ausentes"
              :key="item.id"
              class="mr-2 mb-2"
              small
              outlined
              :color="item.em_ferias ? 'info' : 'grey darken-1'"
            >
              {{ item.nome }}
              <span v-if="item.em_ferias" class="ml-1 font-weight-bold">(férias)</span>
            </v-chip>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <v-card v-else outlined>
      <v-card-text class="text-center py-10 grey--text">
        <v-icon size="48" color="grey lighten-1">mdi-calendar-search</v-icon>
        <div class="mt-3">Escolha uma data para ver as entradas e saídas dos colaboradores.</div>
      </v-card-text>
    </v-card>

    <e-modal-espelho-ponto-pdf
      :dialog="dialogPdf"
      :pdf="pdf"
      :title="`Relatório de Ponto Diário — ${dataLabel}`"
      :filename="nomeArquivoPdf"
      @close="fecharPdf"
    />
  </main-template>
</template>

<script>
import EModalEspelhoPontoPdf from "@/components/Instrutores/EModalEspelhoPontoPdf.vue";

const RESUMO_VAZIO = {
  total_colaboradores: 0,
  presentes: 0,
  presentes_percentual: 0,
  ausentes: 0,
  ausentes_percentual: 0,
  primeiro_chegar: null,
  ultimo_chegar: null,
};

// Data de hoje no horário de Brasília (America/Sao_Paulo, o mesmo fuso da API),
// independente do fuso configurado no computador. "en-CA" formata como AAAA-MM-DD.
const hojeBrasilia = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());

export default {
  name: "RelatorioPontoDiarioPage",
  components: {
    EModalEspelhoPontoPdf,
  },
  data() {
    return {
      data: hojeBrasilia(),
      busca: "",
      carregando: false,
      carregado: false,
      gerandoPdf: false,
      dialogPdf: false,
      pdf: null,
      colaboradores: [],
      ausentes: [],
      resumo: { ...RESUMO_VAZIO },
    };
  },
  computed: {
    dataLabel() {
      if (!this.data) return "";
      const [ano, mes, dia] = this.data.split("-");
      return `${dia}/${mes}/${ano}`;
    },
    nomeArquivoPdf() {
      return `relatorio_ponto_diario_${this.data}.pdf`;
    },
    colaboradoresFiltrados() {
      const termo = (this.busca || "").trim().toLowerCase();
      if (!termo) return this.colaboradores;
      return this.colaboradores.filter((c) => c.nome.toLowerCase().includes(termo));
    },
    countSaidasPendentes() {
      return this.colaboradores.filter((c) => c.saida_pendente).length;
    },
    maxPares() {
      return this.colaboradores.reduce((max, c) => {
        const totalBatidas = c.total_batidas || 0;
        const pares = Math.ceil(totalBatidas / 2);
        return pares > max ? pares : max;
      }, 2);
    },
    headersComputados() {
      const headers = [{ text: "Colaborador", value: "nome" }];
      Array.from({ length: this.maxPares }, (_, idx) => idx + 1).forEach((i) => {
        headers.push({ text: "Entrada", value: `entrada_${i}`, sortable: false });
        headers.push({ text: "Saída", value: `saida_${i}`, sortable: false });
      });
      headers.push({
        text: "Total Registros",
        value: "total_batidas",
        sortable: false,
        align: "center",
      });
      return headers;
    },
  },
  mounted() {
    this.buscar();
  },
  beforeDestroy() {
    if (this.pdf) {
      window.URL.revokeObjectURL(this.pdf);
    }
  },
  methods: {
    async buscar() {
      if (!this.data) {
        this.$toast.danger("Selecione a data do relatório.");
        return;
      }
      try {
        this.carregando = true;
        this.$loaderService.open("Carregando relatório de ponto");
        const relatorio = await this.$services.relatorioPontoService.relatorioDiario({
          data: this.data,
        });
        this.colaboradores = relatorio.colaboradores || [];
        this.ausentes = relatorio.ausentes || [];
        this.resumo = { ...RESUMO_VAZIO, ...(relatorio.resumo || {}) };
        this.carregado = true;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
        this.$loaderService.close();
      }
    },
    irParaHoje() {
      this.data = hojeBrasilia();
      this.buscar();
    },
    async gerarPdf() {
      if (!this.data) {
        this.$toast.danger("Selecione a data do relatório.");
        return;
      }
      try {
        this.gerandoPdf = true;
        this.limparPdfAtual();
        this.pdf = await this.$services.relatorioPontoService.relatorioDiarioPdf({
          data: this.data,
        });
        this.dialogPdf = true;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.gerandoPdf = false;
      }
    },
    fecharPdf() {
      this.dialogPdf = false;
      this.limparPdfAtual();
    },
    limparPdfAtual() {
      if (this.pdf) {
        window.URL.revokeObjectURL(this.pdf);
        this.pdf = null;
      }
    },
    formatarPercentual(valor) {
      const numero = Number(valor) || 0;
      return `${numero.toFixed(1).replace(".", ",")}%`;
    },
  },
};
</script>

<style scoped>
.rel-ponto__chip-data {
  font-weight: 600;
}

.rel-ponto__stat {
  border-left: 3px solid var(--brand) !important;
}

.rel-ponto__stat--presentes {
  border-left-color: #16a34a !important;
}

.rel-ponto__stat--ausentes {
  border-left-color: #dc2626 !important;
}

.rel-ponto__stat--saidas {
  border-left-color: #d97706 !important;
}

.rel-ponto__stat-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--text-muted);
}

.rel-ponto__stat-value {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
  color: var(--text-2);
}

.rel-ponto__stat-value small {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
}

.rel-ponto__extremo-icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.rel-ponto__extremo-icon--first {
  background: linear-gradient(135deg, #16a34a, #4ade80);
}

.rel-ponto__extremo-icon--last {
  background: linear-gradient(135deg, #d97706, #fbbf24);
}

.rel-ponto__extremo-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--text-muted);
}

.rel-ponto__extremo-valor {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-2);
}
</style>
